import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { WARRANTY_REFUNDS_URL, WARRANTY_SUBSCRIPTIONS_URL } from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasOneOfWarrantyPermissions, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import {
  getReceiptLink,
  getSubscription,
  listSubscriptionEvents,
  listSubscriptionPayments,
} from "../common/warrantyApi";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDate, formatDateTime, formatMinor, safeHttpUrl } from "../common/format";
import {
  benefitKindLabel,
  billingModeLabel,
  dateSourceLabel,
  entitlementCodeLabel,
  entitlementPaidLabel,
  entitlementStateLabel,
  entitlementValidLabel,
  partnerStateLabel,
  paymentLineKindLabel,
  paymentPurposeLabel,
  paymentStatusLabel,
  refundReasonLabel,
  subscriptionStatusLabel,
} from "../common/opsLabels";
import VendorPolicyFacts, { hasTheftOrRoadside } from "./VendorPolicyFacts";
import "../warranty.css";

const Facts = ({ rows }) => (
  <dl className="warranty-facts">
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <dt>{label}</dt>
        <dd>{value === null || value === undefined || value === "" ? "-" : value}</dd>
      </React.Fragment>
    ))}
  </dl>
);

const Panel = ({ title, note, children }) => (
  <section className="warranty-panel" aria-label={title}>
    <h5>
      {title}
      {note && <span className="warranty-muted"> {note}</span>}
    </h5>
    {children}
  </section>
);

const PaymentRow = ({ payment, receiptBusy, onReceipt, receiptError }) => {
  const refund =
    payment.refundReason || payment.refundRequestedAt || payment.refundedAt
      ? [
          payment.refundReason ? refundReasonLabel(payment.refundReason) : "Refund",
          payment.refundedAt
            ? `refunded ${formatDate(payment.refundedAt)}`
            : payment.refundRequestedAt
            ? `asked ${formatDate(payment.refundRequestedAt)}, not done yet`
            : "",
          payment.refundAmountMinor !== null
            ? formatMinor(payment.refundAmountMinor, payment.currency)
            : "",
        ]
          .filter(Boolean)
          .join(", ")
      : null;
  return (
    <>
      <tr>
        <td>
          {paymentPurposeLabel(payment.purpose)}
          {payment.instalmentNumber !== null && ` ${payment.instalmentNumber}`}
          <div className="warranty-muted">{formatDateTime(payment.capturedAt)}</div>
        </td>
        <td className="warranty-num">
          {formatMinor(payment.amountMinor, payment.currency)}
          <div className="warranty-muted">incl. tax {formatMinor(payment.taxMinor, payment.currency)}</div>
        </td>
        <td>
          <span className={`warranty-badge is-pay-${payment.status}`}>
            {paymentStatusLabel(payment.status)}
          </span>
          {payment.providerPaymentId && (
            <div className="warranty-muted">{payment.providerPaymentId}</div>
          )}
        </td>
        <td>{refund || "-"}</td>
        <td>
          {payment.lines.length > 0 ? (
            <details className="warranty-lines">
              <summary>
                {payment.lines.length} line{payment.lines.length === 1 ? "" : "s"}
                {payment.linesTruncated ? " (more lines not shown)" : ""}
              </summary>
              <ul>
                {payment.lines.map((line, index) => (
                  <li key={`${line.kind}-${line.benefitKey || ""}-${index}`}>
                    {paymentLineKindLabel(line.kind)}
                    {line.benefitKey ? ` (${line.benefitKey})` : ""}:{" "}
                    {formatMinor(line.amountMinor, payment.currency)}
                  </li>
                ))}
                {payment.linesTruncated && <li className="warranty-muted">and more lines</li>}
              </ul>
            </details>
          ) : (
            "-"
          )}
        </td>
        <td className="warranty-actions-cell">
          {payment.receiptReady ? (
            <button
              type="button"
              className="warranty-inline-btn"
              onClick={() => onReceipt(payment)}
              disabled={receiptBusy}
              aria-label={`Receipt for ${paymentPurposeLabel(payment.purpose)}${
                payment.instalmentNumber !== null ? ` ${payment.instalmentNumber}` : ""
              }`}
            >
              {receiptBusy ? "Opening" : "Receipt"}
            </button>
          ) : (
            <span className="warranty-muted">No receipt</span>
          )}
          {receiptError && (
            <div className="warranty-field-msg is-error" role="alert" style={{ float: "none" }}>
              {receiptError}
            </div>
          )}
        </td>
      </tr>
    </>
  );
};

const SubscriptionDetail = () => {
  const { roomId: subscriptionId } = useParams();
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasOneOfWarrantyPermissions(selfInfo, [WARRANTY_PERMISSION.OPS, WARRANTY_PERMISSION.VIEW]);
  // why: a view-only user sees the plan but takes no action on it.
  const canRefund = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.OPS);
  useWarrantyNav(allowed);

  const [view, setView] = useState({ status: "loading", data: null, message: "" });
  const [payments, setPayments] = useState({ items: [], cursor: null, loading: false, error: "" });
  const [events, setEvents] = useState({ items: [], cursor: null, loading: false, error: "" });
  const [receipt, setReceipt] = useState({ busyId: "", errorId: "", message: "" });
  const mounted = useRef(true);
  const seq = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setView({ status: "loading", data: null, message: "" });
    const result = await getSubscription(accessToken, subscriptionId);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    if (result.ok) {
      setView({ status: "ready", data: result.data, message: "" });
      setPayments({
        items: result.data.payments,
        cursor: result.data.paymentsNextCursor || null,
        loading: false,
        error: "",
      });
      setEvents({
        items: result.data.events,
        cursor: result.data.eventsNextCursor || null,
        loading: false,
        error: "",
      });
    } else {
      setView({ status: "error", data: null, message: result.message, notFound: result.notFound });
    }
  }, [accessToken, subscriptionId]);

  useEffect(() => {
    if (allowed) {
      load();
    }
  }, [allowed, load]);

  const morePayments = async () => {
    if (payments.loading || !payments.cursor) {
      return;
    }
    const mine = seq.current;
    setPayments((prev) => ({ ...prev, loading: true, error: "" }));
    const result = await listSubscriptionPayments(accessToken, subscriptionId, payments.cursor);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    setPayments((prev) =>
      result.ok
        ? {
            items: [...prev.items, ...result.data.items],
            cursor: result.data.nextCursor || null,
            loading: false,
            error: "",
          }
        : { ...prev, loading: false, error: result.message }
    );
  };

  const moreEvents = async () => {
    if (events.loading || !events.cursor) {
      return;
    }
    const mine = seq.current;
    setEvents((prev) => ({ ...prev, loading: true, error: "" }));
    const result = await listSubscriptionEvents(accessToken, subscriptionId, events.cursor);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    setEvents((prev) =>
      result.ok
        ? {
            items: [...prev.items, ...result.data.items],
            cursor: result.data.nextCursor || null,
            loading: false,
            error: "",
          }
        : { ...prev, loading: false, error: result.message }
    );
  };

  // The link is asked for on click and opened in a new tab. The tab is opened first (a popup
  // blocker only allows that inside the click) and pointed at the link when it arrives.
  const openReceipt = async (payment) => {
    if (receipt.busyId !== "") {
      return;
    }
    const tab = window.open("about:blank", "_blank");
    if (!tab) {
      setReceipt({
        busyId: "",
        errorId: payment.id,
        message: "Your browser blocked the new tab. Allow pop-ups for this site and try again.",
      });
      return;
    }
    tab.opener = null;
    setReceipt({ busyId: payment.id, errorId: "", message: "" });
    const result = await getReceiptLink(accessToken, payment.id);
    if (!mounted.current) {
      tab.close();
      return;
    }
    const url = result.ok ? safeHttpUrl(result.data?.url) : null;
    if (url) {
      tab.location.href = url;
      setReceipt({ busyId: "", errorId: "", message: "" });
    } else {
      tab.close();
      setReceipt({
        busyId: "",
        errorId: payment.id,
        message: result.ok ? "The receipt link is not usable." : result.message,
      });
      if (result.ok) {
        toast.error("The receipt link is not usable.");
      }
    }
  };

  const data = view.data;
  const sub = data?.subscription;
  const reg = data?.registration;

  return (
    <WarrantyPage
      title={reg ? `Subscription: ${reg.frameNumber}` : "Subscription"}
      sub={
        sub
          ? `${sub.displayName}, ${billingModeLabel(sub.billingMode).toLowerCase()}, market ${sub.country}. Read only.`
          : "Read only."
      }
      allowed={allowed}
      deniedTitle="You do not have access to subscriptions"
      deniedText="Ask an admin to give you the Warranty Operations permission."
      actions={
        <>
          <Link className="warranty-inline-btn" to={WARRANTY_SUBSCRIPTIONS_URL}>
            Back to search
          </Link>
          {sub && canRefund && (
            <Link
              className="warranty-inline-btn is-primary"
              to={`${WARRANTY_REFUNDS_URL}?subscription=${encodeURIComponent(sub.id)}`}
            >
              Refund
            </Link>
          )}
        </>
      }
    >
      {view.status === "loading" && <Loading label="Loading the subscription" />}
      {view.status === "error" && (
        <ErrorState message={view.message} onRetry={view.notFound ? undefined : load} />
      )}
      {view.status === "ready" && (
        <>
          <div className="warranty-two-col">
            <Panel title="Plan">
              <Facts
                rows={[
                  ["Status", subscriptionStatusLabel(sub.status)],
                  ["Plan", `${sub.displayName} (${sub.tier}, rank ${sub.tierRank})`],
                  ["Terms version", sub.termsVersion],
                  ["Billing", billingModeLabel(sub.billingMode)],
                  ["Tenure", `${sub.tenureMonths} months`],
                  ["Months paid", sub.monthsPaid],
                  ["Provider", `${sub.provider}${sub.hasMandate ? ", has a standing mandate" : ""}`],
                  ["Started", formatDateTime(sub.startedAt)],
                  ["Paid through", formatDate(sub.paidThrough)],
                  ["Next due", formatDate(sub.nextDueOn)],
                  ["Grace ends", formatDate(sub.graceEndsOn)],
                  ["Cancel asked", formatDateTime(sub.cancelRequestedAt)],
                  ["Upgrade window ends", formatDate(sub.upgradeWindowEndsOn)],
                  ["Upgraded", formatDateTime(sub.upgradedAt)],
                  ["Refunded", formatDateTime(sub.refundedAt)],
                ]}
              />
            </Panel>
            <Panel title="Bike">
              <Facts
                rows={[
                  ["Frame number", reg.frameNumber],
                  ["Product", `${reg.productName} (${reg.productGroup})`],
                  ["Phone", reg.maskedPhone],
                  ["Purchase date", formatDate(reg.purchaseDate)],
                  [
                    "Purchase date source",
                    reg.purchaseDateSource ? dateSourceLabel(reg.purchaseDateSource) : "-",
                  ],
                  ["Registration status", reg.status],
                ]}
              />
            </Panel>
          </div>

          <Panel title="Payments">
            {payments.items.length === 0 ? (
              <div className="warranty-muted">No payments yet.</div>
            ) : (
              <div className="tableView warranty-ops-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Payment</th>
                      <th scope="col" className="warranty-num">
                        Amount
                      </th>
                      <th scope="col">Status</th>
                      <th scope="col">Refund</th>
                      <th scope="col">Lines</th>
                      <th scope="col">
                        <span className="warranty-sr-only">Receipt</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.items.map((payment) => (
                      <PaymentRow
                        key={payment.id}
                        payment={payment}
                        receiptBusy={receipt.busyId === payment.id}
                        receiptError={receipt.errorId === payment.id ? receipt.message : ""}
                        onReceipt={openReceipt}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="warranty-more">
              {payments.error && (
                <span className="warranty-field-msg is-error" role="alert">
                  {payments.error}
                </span>
              )}
              {payments.cursor && (
                <Button className="Addbtn" onClick={morePayments} disabled={payments.loading}>
                  {payments.loading ? "Loading" : "Load more payments"}
                </Button>
              )}
            </div>
          </Panel>

          <Panel
            title="Entitlements"
            note={data.entitlementsTruncated ? "(showing the first 16)" : ""}
          >
            {data.entitlements.length === 0 ? (
              <div className="warranty-muted">No benefits.</div>
            ) : (
              <div className="tableView warranty-ops-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Benefit</th>
                      <th scope="col">State</th>
                      <th scope="col">How paid</th>
                      <th scope="col">Valid</th>
                      <th scope="col">Months</th>
                      <th scope="col">Other</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entitlements.map((item) => (
                      <tr key={item.id}>
                        <td>
                          {benefitKindLabel(item.kind)}
                          <div className="warranty-muted">{item.benefitKey}</div>
                        </td>
                        <td>
                          <span className={`warranty-badge is-ent-${item.state}`}>
                            {entitlementStateLabel(item.state)}
                          </span>
                        </td>
                        <td>{entitlementPaidLabel(item.chargeModel, sub.billingMode)}</td>
                        <td>{entitlementValidLabel(item)}</td>
                        <td>
                          {item.monthsCap !== null
                            ? `${item.monthsGranted} of ${item.monthsCap}`
                            : item.monthsGranted}
                        </td>
                        <td>
                          {[
                            entitlementCodeLabel(item.state, item.hasCode),
                            item.valueMinor !== null ? `Value ${formatMinor(item.valueMinor, sub.currency)}` : "",
                            item.opsReference ? `Ref ${item.opsReference}` : "",
                            item.consumedAt ? `Used ${formatDate(item.consumedAt)}` : "",
                          ]
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {hasTheftOrRoadside(data.entitlements) && (
            <Panel title="Vendor policy" note="(theft and roadside)">
              <VendorPolicyFacts accessToken={accessToken} subscriptionId={sub.id} />
            </Panel>
          )}

          {data.partnerPolicies.length > 0 && (
            <Panel
              title="Partner policies"
              note={data.partnerPoliciesTruncated ? "(showing the first 4)" : ""}
            >
              <div className="tableView warranty-ops-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Cover</th>
                      <th scope="col">Insurer</th>
                      <th scope="col">State</th>
                      <th scope="col">Policy number</th>
                      <th scope="col">Valid</th>
                      <th scope="col" className="warranty-num">
                        Paid
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.partnerPolicies.map((policy) => (
                      <tr key={policy.id}>
                        <td>{benefitKindLabel(policy.benefitKind)}</td>
                        <td>{policy.partnerKey || "-"}</td>
                        <td>
                          {partnerStateLabel(policy.state)}
                          {policy.late && (
                            <>
                              {" "}
                              <span className="warranty-badge is-warning">Late</span>
                            </>
                          )}
                          {policy.neverActivated && (
                            <>
                              {" "}
                              <span className="warranty-badge is-warning">Never activated</span>
                            </>
                          )}
                        </td>
                        <td>{policy.policyNumber || "-"}</td>
                        <td>
                          {policy.validFrom || policy.validTo
                            ? `${formatDate(policy.validFrom)} to ${formatDate(policy.validTo)}`
                            : "-"}
                        </td>
                        <td className="warranty-num">
                          {formatMinor(policy.amountMinor, policy.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          <Panel title="Audit trail" note="newest first">
            {events.items.length === 0 ? (
              <div className="warranty-muted">Nothing recorded yet.</div>
            ) : (
              <div className="tableView warranty-ops-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">When</th>
                      <th scope="col">What</th>
                      <th scope="col">About</th>
                      <th scope="col">By</th>
                      <th scope="col">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.items.map((event) => (
                      <tr key={event.id}>
                        <td>{formatDateTime(event.at)}</td>
                        <td>{event.kind}</td>
                        <td>{event.entityType}</td>
                        <td>
                          {event.actor.actorType || event.actor.kind}
                          {event.actor.actorId ? ` ${event.actor.actorId}` : ""}
                        </td>
                        <td>
                          {event.reason ? (
                            <>
                              {event.reason}
                              {event.reasonTruncated && <span className="warranty-muted"> (cut)</span>}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="warranty-more">
              {events.error && (
                <span className="warranty-field-msg is-error" role="alert">
                  {events.error}
                </span>
              )}
              {events.cursor && (
                <Button className="Addbtn" onClick={moreEvents} disabled={events.loading}>
                  {events.loading ? "Loading" : "Load more events"}
                </Button>
              )}
            </div>
          </Panel>
        </>
      )}
    </WarrantyPage>
  );
};

export default SubscriptionDetail;
